import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import ReactCountryFlag from 'react-country-flag';
import { SUPPORTED_LANGUAGES } from '@employ-engage/data';

const languages = SUPPORTED_LANGUAGES;

export const LanguagePicker = () => {
  const { i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);

  useEffect(() => {
    if (!i18n?.changeLanguage) return;
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      const initialLanguage =
        languages.find((lang) => lang.id === savedLanguage) || languages[0];
      setSelectedLanguage(initialLanguage);
      i18n.changeLanguage(initialLanguage.id);
      document.documentElement.setAttribute('dir', initialLanguage.dir);
    } else {
      const browserLanguage = navigator.language.split('-')[0];
      const initialLanguage =
        languages.find((lang) => lang.id === browserLanguage) || languages[0];
      setSelectedLanguage(initialLanguage);
      i18n.changeLanguage(initialLanguage.id);
      document.documentElement.setAttribute('dir', initialLanguage.dir);
    }
  }, [i18n]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    const newSelectedLanguage =
      languages.find((lang) => lang.id === lng) || languages[0];
    setSelectedLanguage(newSelectedLanguage);
    localStorage.setItem('selectedLanguage', lng);
    document.documentElement.setAttribute('dir', newSelectedLanguage.dir);
  };

  return (
    <Listbox
      value={selectedLanguage}
      onChange={(lang) => changeLanguage(lang.id)}
    >
      <div className="relative">
        <Listbox.Button className="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm">
          <div className="col-start-1 row-start-1 truncate pr-6 flex items-center gap-2">
            <ReactCountryFlag countryCode={selectedLanguage.flag} />
            <span>{selectedLanguage.name}</span>
          </div>
          <ChevronUpDownIcon
            aria-hidden="true"
            className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
          />
        </Listbox.Button>

        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full min-w-max overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
          {languages.map((language) => (
            <Listbox.Option
              key={language.id}
              value={language}
              className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white data-[focus]:outline-none"
            >
              <span className="truncate font-normal group-data-[selected]:font-semibold flex items-center">
                <div className="col-start-1 row-start-1 truncate pr-6 flex items-center gap-2">
                  <ReactCountryFlag countryCode={language.flag} />
                  <span>{language.name}</span>
                </div>
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                <CheckIcon aria-hidden="true" className="size-5" />
              </span>
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </div>
    </Listbox>
  );
};
